
import { getJobsBookedByCustomer, getJobsBookedByUser, getJobsBookedLog } from '@/api';
import DatePicker from '@/components/searchPage/DatePicker.vue';
import BackButton from '@/components/UI/backButton/BackButton.vue';
import { Header, JobsBookedApiData, JobsBookedData, JobsBookedResponse, User } from '@/models/main';
import { addWeeks } from 'date-fns';
import { format, utcToZonedTime } from 'date-fns-tz';
import * as routeNames from '@/router/routeNames';

import { Component, Vue } from 'vue-property-decorator';
import { mapState } from 'vuex';
import { notificationErrorText } from '@/utils/PortalConstants';
import VueJsonPretty from 'vue-json-pretty';
import 'vue-json-pretty/lib/styles.css';
import { timeZoneDefault } from '@/utils/PortalConstants';
import { formatDateStringWithTZ } from '@/utils/InputProcessingUtilities';

@Component({
  components: {
    BackButton,
    DatePicker,
    VueJsonPretty,
  },
  computed: {
    ...mapState('moduleUser', { user: 'user' }),
  },
})
export default class JobsBooked extends Vue {
  public routeNames = routeNames;
  private loading = false;
  private loadingExpanded = false;
  private datesDefault: string[] = [];
  private dates: string[] = [];
  private user: User;
  // to use func in the template
  private formatDateStringWithTZ = formatDateStringWithTZ;
  private username = '';
  private customerCode = '';
  private customerChoosen = false;
  private tableTitle = 'My';
  private jobs: JobsBookedData[] = [];
  private expanded: JobsBookedData[] | JobsBookedApiData = [];
  private expandedIsOpen = false;
  private expandedJobId = 0;

  private pagination = {
    page: 1,
    perPage: 20,
    totalPages: 0,
    totalCount: 0,
  };
  private perPageOptions = [10, 20, 50, 100];

  private headers: Header[] = [
    { text: 'Id', value: 'id', align: 'left' },
    { text: 'Date', value: 'date', align: 'left' },
    { text: 'Source', value: 'source', align: 'left' },
    { text: 'Created by', value: 'created_by', align: 'left' },
    { text: 'Created at', value: 'created_at', align: 'left' },
    { text: 'Status', value: 'status', align: 'left' },
    { text: 'Link', value: 'link', align: 'left', sortable: false },
  ];

  private changeDate(dates): void {
    this.dates = dates;
    this.pagination.page = 1;
    this.getJobsWrapper(this.username, dates, this.customerChoosen);
  }

  private clearDate() {
    this.dates = this.datesDefault;
  }

  private setUsername(): void {
    this.pagination.page = 1;
    this.getJobsWrapper(this.username, this.dates, this.customerChoosen);
  }

  private clearUsername(): void {
    this.username = '';
    this.pagination.page = 1;
    this.getJobsWrapper(this.username, this.dates, this.customerChoosen);
  }

  private toggleChange(): void {
    this.expandedIsOpen = false;
    this.pagination.page = 1;
    this.getJobsWrapper(this.currentQuery, this.dates, this.customerChoosen);
  }

  private expandRow(job: JobsBookedData): void {
    if (this.expandedIsOpen && this.expandedJobId === job.id) {
      this.expanded = [];
      this.expandedIsOpen = false;
    } else {
      this.expandedIsOpen = true;
      this.expanded = [];
      this.loadingExpanded = true;
      this.expanded.push(job);
      this.expandedJobId = job.id;

      getJobsBookedLog(job.log_id)
        .then(res => {
          this.expanded[0] = res;
          this.$forceUpdate();
        })
        .catch(() => {
          this.$notify({
            group: 'network',
            title: 'Error getting the log',
            text: notificationErrorText,
            type: 'warn',
          });
        })
        .finally(() => {
          this.loadingExpanded = false;
        });
    }
  }

  private getJobsWrapper(user: string, dates: string[], isCustomer): void {
    this.loading = true;
    const request = isCustomer
      ? getJobsBookedByCustomer(user, dates, this.pagination.page, this.pagination.perPage)
      : getJobsBookedByUser(user, dates, this.pagination.page, this.pagination.perPage);

    request
      .then((res: JobsBookedResponse) => {
        const userTimeZone = this.user.timezone || timeZoneDefault;
        this.jobs = res.results.map((job: JobsBookedApiData) => ({
          id: job.rockhopper_id,
          log_id: job.id,
          date: job.rockhopper_date,
          created_by: job.created_by,
          created_at: `${format(utcToZonedTime(new Date(job.created_at), userTimeZone), 'yyyy-MM-dd HH:mm:ss')}`,
          source: job.source_text,
          status: job.status,
        }));
        this.pagination.totalCount = res.count;
        this.pagination.totalPages = Math.ceil(res.count / this.pagination.perPage);
        this.tableTitle = user || 'My';
      })
      .catch(() => {
        this.$notify({
          group: 'network',
          title: 'Error getting the jobs',
          text: notificationErrorText,
          type: 'warn',
        });
      })
      .finally(() => {
        this.loading = false;
      });
  }

  private onPageChange(): void {
    this.getJobsWrapper(this.currentQuery, this.dates, this.customerChoosen);
  }

  private onPerPageChange(): void {
    this.pagination.page = 1;
    this.getJobsWrapper(this.currentQuery, this.dates, this.customerChoosen);
  }

  private get currentQuery(): string {
    return this.customerChoosen ? this.customerCode : this.username;
  }

  private mounted(): void {
    const userTimeZone = this.user.timezone || timeZoneDefault;
    const fromDate = format(utcToZonedTime(addWeeks(new Date(), -2), userTimeZone), 'yyyy-MM-dd'); // today
    const toDate = format(utcToZonedTime(new Date(), userTimeZone), 'yyyy-MM-dd'); // today
    this.datesDefault = [fromDate, toDate];
    this.dates = [...this.datesDefault];
    this.customerCode = this.user.customerCode;
    this.getJobsWrapper('', this.datesDefault, this.customerChoosen);
  }
}
